import React from 'react';
import { Pencil, Trash2, Eye, EyeOff, Star, StarOff, ShoppingCart, Ban, CheckSquare, Square, Leaf } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { DualScrollTable } from '../../DualScrollTable';
import { formatPrice } from '../../../lib/utils';
import { getPackageDisplay } from '../../../lib/packageUtils';

export const ProductTable = ({
    products, categories, isSelected, toggleItem, toggleAll, allSelected,
    handleToggleActive, handleToggleWeeklyOffer, handleToggleReadyToOrder,
    handleEdit, handleDeleteClick
}) => {
    const getCategoryName = (categoryId) => categories.find(c => c.id === categoryId)?.name || categoryId;

    if (products.length === 0) {
        return (
            <div className="text-center py-12">
                <Leaf className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                <p className="text-muted-foreground">Aucun produit trouve</p>
            </div>
        );
    }

    return (
        <DualScrollTable>
            <Table>
                <TableHeader>
                    <TableRow className="border-jungle/10">
                        <TableHead className="w-10">
                            <button onClick={toggleAll} className="p-1 hover:bg-jungle/10 rounded" data-testid="select-all-products">
                                {allSelected ? <CheckSquare className="w-4 h-4 text-jungle" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                            </button>
                        </TableHead>
                        <TableHead className="text-jungle/70">Code</TableHead>
                        <TableHead className="text-jungle/70">GENCOD</TableHead>
                        <TableHead className="text-jungle/70">Image</TableHead>
                        <TableHead className="text-jungle/70">Nom</TableHead>
                        <TableHead className="text-jungle/70">Calibre</TableHead>
                        <TableHead className="text-jungle/70">Prix / Unite</TableHead>
                        <TableHead className="text-jungle/70">Stock</TableHead>
                        <TableHead className="text-jungle/70 text-center">Visible</TableHead>
                        <TableHead className="text-jungle/70 text-center">Offre Semaine</TableHead>
                        <TableHead className="text-jungle/70 text-center">Commande</TableHead>
                        <TableHead className="text-jungle/70 text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {products.map((product) => (
                        <TableRow
                            key={product.id}
                            className={`border-jungle/10 ${!product.is_active ? 'opacity-50 bg-gray-50' : ''} ${isSelected(product.id) ? 'bg-jungle/5' : ''}`}
                            data-testid={`product-row-${product.id}`}
                        >
                            <TableCell>
                                <button onClick={() => toggleItem(product.id)} className="p-1 hover:bg-jungle/10 rounded" data-testid={`select-product-${product.id}`}>
                                    {isSelected(product.id) ? <CheckSquare className="w-4 h-4 text-jungle" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                                </button>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className="font-mono text-xs rounded-full border-jungle/30">{product.product_code || '-'}</Badge>
                            </TableCell>
                            <TableCell>
                                <span className="font-mono text-xs text-muted-foreground">{product.gen_code || '-'}</span>
                            </TableCell>
                            <TableCell>
                                <div className="relative">
                                    <img src={product.image_url} alt={product.name} className="w-14 h-14 object-cover rounded-lg" />
                                    {product.is_weekly_offer && (
                                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                                            <Star className="w-3 h-3 text-white fill-white" />
                                        </div>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div>
                                    <p className="font-medium text-jungle">{product.name}</p>
                                    <p className="text-xs text-muted-foreground">{getCategoryName(product.category_id)}</p>
                                </div>
                            </TableCell>
                            <TableCell>
                                <span className="text-sm text-muted-foreground">{product.calibre || '-'}</span>
                            </TableCell>
                            <TableCell>
                                <div className="font-semibold text-leaf">{formatPrice(product.price)}</div>
                                <div className="text-xs text-muted-foreground">/ {product.unit}</div>
                                {product.package_weight > 0 && (
                                    <div className="text-xs text-amber-600 mt-0.5">{getPackageDisplay(product.package_weight, product.unit)}/colis</div>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant={product.stock > 10 ? 'default' : 'destructive'} className="rounded-full">{product.stock}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(product.id)}
                                    className={product.is_active ? 'text-leaf hover:bg-leaf/10' : 'text-gray-400 hover:bg-gray-100'}
                                    data-testid={`toggle-active-${product.id}`}>
                                    {product.is_active ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                                </Button>
                            </TableCell>
                            <TableCell className="text-center">
                                <Button variant="ghost" size="sm" onClick={() => handleToggleWeeklyOffer(product.id)}
                                    className={product.is_weekly_offer ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}
                                    data-testid={`toggle-weekly-${product.id}`}>
                                    {product.is_weekly_offer ? <Star className="h-5 w-5 fill-amber-500" /> : <StarOff className="h-5 w-5" />}
                                </Button>
                            </TableCell>
                            <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-0.5">
                                    <Button variant="ghost" size="sm" onClick={() => { if (!product.ready_to_order) handleToggleReadyToOrder(product.id); }}
                                        className={product.ready_to_order ? 'text-jungle bg-jungle/10' : 'text-gray-300 hover:text-jungle hover:bg-jungle/10'}
                                        title="Prêt à commander"
                                        data-testid={`toggle-orderable-${product.id}`}>
                                        <ShoppingCart className={`h-4 w-4 ${product.ready_to_order ? 'fill-jungle/20' : ''}`} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => { if (product.ready_to_order) handleToggleReadyToOrder(product.id); }}
                                        className={!product.ready_to_order ? 'text-red-500 bg-red-50' : 'text-gray-300 hover:text-red-500 hover:bg-red-50'}
                                        title="Non prêt à commander"
                                        data-testid={`toggle-not-orderable-${product.id}`}>
                                        <Ban className="h-4 w-4" />
                                    </Button>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} className="hover:bg-leaf/10" data-testid={`edit-product-${product.id}`}>
                                        <Pencil className="h-4 w-4 text-leaf" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => handleDeleteClick(product)} data-testid={`delete-product-${product.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </DualScrollTable>
    );
};
